const SMART_QUOTES: Record<string, string> = {
  '\u2018': "'",
  '\u2019': "'",
  '\u201C': '"',
  '\u201D': '"',
  '\u2013': '-',
  '\u2014': '-',
  '\u2026': '...',
};

export function normalizeQuotes(value: string): string {
  return value.replace(/[\u2018\u2019\u201C\u201D\u2013\u2014\u2026]/g, (ch) => SMART_QUOTES[ch] ?? ch);
}

export function collapseWhitespace(value: string): string {
  return value.replace(/\s+/g, ' ').trim();
}

/** Uppercase, punctuation-free key used to match two spellings of the same character. */
export function characterKey(name: string): string {
  return collapseWhitespace(normalizeQuotes(name))
    .toUpperCase()
    .replace(/[^A-Z0-9 ]/g, '')
    .trim();
}

export function toTitleCase(value: string): string {
  return collapseWhitespace(value.toLowerCase()).replace(
    /(^|[\s'-])([a-z])/g,
    (_, boundary: string, ch: string) => boundary + ch.toUpperCase(),
  );
}

/** True when the letters present are overwhelmingly uppercase. Ignores digits and punctuation. */
export function isUpperCaseDominant(value: string): boolean {
  const letters = value.replace(/[^A-Za-z]/g, '');
  if (letters.length === 0) return false;
  const upper = letters.replace(/[^A-Z]/g, '').length;
  return upper / letters.length >= 0.85;
}

export function wordCount(value: string): number {
  const trimmed = collapseWhitespace(value);
  return trimmed === '' ? 0 : trimmed.split(' ').length;
}

export function words(value: string): string[] {
  return collapseWhitespace(value).split(' ').filter(Boolean);
}

/** Trailing `count` words of a line, used to build rehearsal cues. */
export function lastWords(value: string, count: number): string {
  const all = words(value);
  if (all.length <= count) return all.join(' ');
  return all.slice(all.length - count).join(' ');
}

/** Lowercased, punctuation-stripped tokens for recall scoring. */
export function tokenize(value: string): string[] {
  return normalizeQuotes(value)
    .toLowerCase()
    .replace(/[^a-z0-9\s']/g, ' ')
    .split(/\s+/)
    .filter(Boolean);
}

export function pluralize(count: number, singular: string, plural = `${singular}s`): string {
  return count === 1 ? singular : plural;
}
